/**
 * Frontend Logger
 *
 * Story-telling approach: Logs should tell a story of what happened,
 * why it happened, and how the user/system responded.
 *
 * Outputs to:
 * - Console (always, for development)
 * - Backend /api/log endpoint (for persistence in logs/app.log)
 *
 * All logs are tagged with source="frontend" in the backend.
 * Includes file and function info for easier debugging.
 */

const LOG_LEVELS = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
};

// Default to INFO in production, DEBUG in development
const currentLevel =
  process.env.NODE_ENV === 'production' ? LOG_LEVELS.INFO : LOG_LEVELS.DEBUG;

// Only send INFO and above to backend (avoid flooding with DEBUG)
const BACKEND_MIN_LEVEL = LOG_LEVELS.INFO;

/**
 * Extract caller info from stack trace
 * Returns { file, function } or nulls if can't parse
 */
const getCallerInfo = () => {
  try {
    const err = new Error();
    const stack = err.stack?.split('\n') || [];

    // Stack format varies by browser, but typically:
    // [0] "Error"
    // [1] "at getCallerInfo (...)"
    // [2] "at log (...)"
    // [3] "at Object.info (...)" <- logger method
    // [4] "at actualCaller (...)" <- what we want

    // Find the first line that's not from this file (logger.js)
    for (let i = 1; i < stack.length; i++) {
      const line = stack[i];
      if (!line.includes('logger.js') && !line.includes('Logger')) {
        // Try to extract function and file
        // Format: "at functionName (file.js:line:col)" or "at file.js:line:col"
        const match = line.match(/at\s+(?:(.+?)\s+\()?(.+?):(\d+):\d+\)?/);
        if (match) {
          const func = match[1] || 'anonymous';
          const fullPath = match[2] || '';
          // Extract just the filename from path
          const file = fullPath.split('/').pop()?.split('?')[0] || 'unknown';
          return { file, function: func };
        }
      }
    }
  } catch (e) {
    // Stack trace parsing failed, return defaults
  }
  return { file: 'unknown', function: 'unknown' };
};

/**
 * Format log message for console
 */
const formatConsoleMessage = (level, context, message, data = {}, callerInfo = {}) => {
  const timestamp = new Date().toISOString();
  const location = callerInfo.file ? `${callerInfo.file}:${callerInfo.function}` : '';
  const dataStr = Object.keys(data).length > 0
    ? ` | ${Object.entries(data).map(([k, v]) => `${k}=${JSON.stringify(v)}`).join(', ')}`
    : '';

  return `[${timestamp}] ${level}: [${context}] ${message}${dataStr}${location ? ` (${location})` : ''}`;
};

/**
 * Send log to backend (fire and forget)
 */
const sendToBackend = async (level, context, message, data, callerInfo) => {
  try {
    await fetch('/api/log', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        level,
        context,
        message,
        data: {
          ...data,
          _file: callerInfo.file,
          _function: callerInfo.function,
        },
      }),
    });
  } catch (err) {
    // Silently fail - don't create infinite loop by logging the error
    console.warn('[Logger] Failed to send log to backend:', err.message);
  }
};

/**
 * Log to console and optionally to backend
 */
const log = (level, levelName, context, message, data = {}) => {
  if (level < currentLevel) return;

  // Get caller info for debugging
  const callerInfo = getCallerInfo();

  const formattedMessage = formatConsoleMessage(levelName, context, message, data, callerInfo);

  // Always log to console
  switch (levelName) {
    case 'ERROR':
      console.error(formattedMessage);
      break;
    case 'WARN':
      console.warn(formattedMessage);
      break;
    case 'DEBUG':
      console.debug(formattedMessage);
      break;
    default:
      console.log(formattedMessage);
  }

  // Send INFO and above to backend for persistence
  if (level >= BACKEND_MIN_LEVEL) {
    sendToBackend(levelName, context, message, data, callerInfo);
  }
};

/**
 * Logger with context
 * Usage: const logger = createLogger('ChatContainer');
 *        logger.info('Message sent', { messageLength: 50 });
 */
export const createLogger = (context) => ({
  debug: (message, data = {}) => log(LOG_LEVELS.DEBUG, 'DEBUG', context, message, data),
  info: (message, data = {}) => log(LOG_LEVELS.INFO, 'INFO', context, message, data),
  warn: (message, data = {}) => log(LOG_LEVELS.WARN, 'WARN', context, message, data),
  error: (message, data = {}) => log(LOG_LEVELS.ERROR, 'ERROR', context, message, data),
});

// Default logger for general use
export const logger = createLogger('APP');

export default logger;
